import { useTemplateContext } from "shared/app/VerificationTemplate/provider";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  usePopoverContext
} from "shared/components/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import GearWithPointer from "shared/icons/gear-with-pointer.svg?react";
import { ExecutionModes, VerificationNames } from "shared/types";
import { cx } from "shared/utils";
import {
  Check,
  ChevronDown,
  CursorClick01,
  Scales01
} from "untitledui-js-base";

const ExecutionModeSelectorContent = ({
  executionMode,
  updateExecutionMode,
  serviceName,
  readOnly
}: {
  readOnly: boolean;
  executionMode?: ExecutionModes;
  serviceName: VerificationNames;
  updateExecutionMode?: (
    executionMode: ExecutionModes,
    schemaName: VerificationNames
  ) => void;
}) => {
  const { template } = useTemplateContext();
  const ctx = usePopoverContext();

  const isAutomatic = executionMode === ExecutionModes.Automatic;
  const isManual = executionMode === ExecutionModes.Manual;
  const isConditionally = executionMode === ExecutionModes.Conditionally;
  const isExecutionModeEmpty = executionMode === undefined;

  const isConditionallyModeDisabled =
    template?.recommendation_method !== "rule_based";

  return (
    <>
      <PopoverTrigger disabled={readOnly}>
        <Button
          size={ButtonSize.SM}
          Icon={
            isAutomatic || isExecutionModeEmpty
              ? GearWithPointer
              : isManual
                ? CursorClick01
                : Scales01
          }
          variant={readOnly ? ButtonVariant.TEXT : ButtonVariant.BASE}
          disabled={readOnly}
          className={cx(
            readOnly &&
              "flex gap-2 items-center px-3.5 !stroke-content-1000 !text-content-1000"
          )}
        >
          <div className="flex items-center gap-2">
            {isAutomatic || isExecutionModeEmpty
              ? "Run every time"
              : isManual
                ? "Run manually"
                : isConditionally && "Run conditionally"}

            <Show if={!readOnly}>
              <ChevronDown
                className={cx(
                  "stroke-content-100 transition-all w-5",
                  ctx.open && "rotate-180"
                )}
              />
            </Show>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="mt-2 border rounded-lg bg-white cursor-pointer z-[50] max-w-[600px]">
        <div
          className="flex w-full items-center gap-4 py-4 px-6 hover:bg-alpha-40"
          onClick={() => {
            updateExecutionMode?.(ExecutionModes.Automatic, serviceName);
            ctx.setOpen(false);
          }}
        >
          <GearWithPointer className="w-6" />

          <div className="w-full">
            <p className="text-content-1000 text-base font-medium">
              Run every time
            </p>
            <p className="text-content-400 text-sm font-normal">
              Automatically run the report each time an application is submitted
            </p>
          </div>

          <Show if={isAutomatic}>
            <div className="w-6">
              <Check />
            </div>
          </Show>
        </div>

        <Show if={serviceName !== VerificationNames.CreditReport}>
          <Tooltip placement="left">
            <TooltipTrigger
              className={cx(
                "flex items-center gap-4 py-4 px-6 w-full text-left",
                isConditionallyModeDisabled
                  ? "bg-alpha-40"
                  : "hover:bg-alpha-40"
              )}
              onClick={() => {
                if (isConditionallyModeDisabled) return null;
                updateExecutionMode?.(
                  ExecutionModes.Conditionally,
                  serviceName
                );
                ctx.setOpen(false);
              }}
            >
              <Scales01
                className={cx(
                  "w-6",
                  isConditionallyModeDisabled
                    ? "stroke-content-100"
                    : "stroke-content-1000"
                )}
              />

              <div className="text-wrap w-full">
                <p
                  className={cx(
                    "text-base font-medium w-full",
                    isConditionallyModeDisabled
                      ? "text-content-100"
                      : "text-content-1000"
                  )}
                >
                  Run conditionally
                </p>
                <p
                  className={cx(
                    "text-sm font-normal",
                    isConditionallyModeDisabled
                      ? "text-content-100"
                      : "text-content-400"
                  )}
                >
                  Run the report only after all other reports are ready,
                  including manual reports, and only if application was not able
                  to be declined
                </p>
              </div>

              <Show if={isConditionally}>
                <div className="w-6">
                  <Check />
                </div>
              </Show>
            </TooltipTrigger>

            <Show if={template?.recommendation_method !== "rule_based"}>
              <TooltipContent className="max-w-[480px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted shadow-xs">
                <p>
                  To be able to use conditional reports, you must set rule-based
                  criteria
                </p>
              </TooltipContent>
            </Show>
          </Tooltip>
        </Show>

        <div
          className="flex w-full items-center gap-4 py-4 px-6 border-t border-alpha-40 hover:bg-alpha-40 shadow-xl"
          onClick={() => {
            updateExecutionMode?.(ExecutionModes.Manual, serviceName);
            ctx.setOpen(false);
          }}
        >
          <CursorClick01 className="w-6" />
          <div className="w-full">
            <p className="text-content-1000 text-base font-medium">
              Run manually
            </p>
            <p className="text-content-400 text-sm font-normal">
              Manually run the report if needed after an application is
              submitted
            </p>
          </div>

          <Show if={isManual}>
            <div className="w-6">
              <Check />
            </div>
          </Show>
        </div>
      </PopoverContent>
    </>
  );
};

export const ExecutionModeSelector = ({
  executionMode,
  updateExecutionMode,
  serviceName,
  readOnly
}: {
  readOnly: boolean;
  executionMode?: ExecutionModes;
  serviceName: VerificationNames;
  updateExecutionMode?: (
    executionMode: ExecutionModes,
    schemaName: VerificationNames
  ) => void;
}) => {
  return (
    <Popover placement="bottom-end">
      <ExecutionModeSelectorContent
        executionMode={executionMode}
        updateExecutionMode={updateExecutionMode}
        serviceName={serviceName}
        readOnly={readOnly}
      />
    </Popover>
  );
};
